import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IGift } from "@/api_framework/api_modals/GiftConfig";
import { useGiftSetupAPI } from "@/app/hooks/api_hooks/gift/useGiftSetupAPI";
import { useRouter } from "@/app/hooks/useRouter";
import { DeleteGiftModal, GiftTable, GiftToolbar, useGiftColumns } from "./GiftTable";
import { GiftStatusFilter } from "../interface";

const GiftList = () => {
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const { giftList, isListLoading, listError, isDeleting, fetchGiftList, deleteGift } = useGiftSetupAPI();

  const [statusFilter, setStatusFilter] = useState<GiftStatusFilter>("ALL");
  const [deleteTarget, setDeleteTarget] = useState<IGift | null>(null);

  useEffect(() => {
    fetchGiftList();
  }, [fetchGiftList]);

  const visibleGifts = useMemo(
    () => (statusFilter === "ALL" ? giftList : giftList.filter((gift) => gift.status === statusFilter)),
    [giftList, statusFilter],
  );

  const goToGift = useCallback(
    (giftId?: string) => {
      const creationUrl = getRouteKey("GIFT_SETUP_CREATION", "url");
      navigate(giftId ? `${creationUrl}?${new URLSearchParams({ gift_id: giftId }).toString()}` : creationUrl);
    },
    [getRouteKey, navigate],
  );

  const addGift = useCallback(() => goToGift(), [goToGift]);

  const columns = useGiftColumns({ onEdit: goToGift, onDelete: setDeleteTarget });

  const clearFilters = useCallback(() => setStatusFilter("ALL"), []);

  const confirmDelete = async () => {
    if (!deleteTarget?.gift_id) return;
    if (await deleteGift(deleteTarget.gift_id)) setDeleteTarget(null);
  };

  return (
    <div className="px-5 py-4">
      <div className="mb-4">
        <h1 className="text-lg font-semibold leading-7 text-gray-900">Gift Setup</h1>
      </div>

      <section className="w-full min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <GiftToolbar statusFilter={statusFilter} onStatusFilterChange={setStatusFilter} onAddGift={addGift} />
        <GiftTable
          columns={columns}
          gifts={visibleGifts}
          isLoading={isListLoading}
          error={listError}
          hasFilters={statusFilter !== "ALL"}
          onRetry={fetchGiftList}
          onClearFilters={clearFilters}
          onAddGift={addGift}
        />
      </section>

      <DeleteGiftModal
        target={deleteTarget}
        isLoading={isDeleting}
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default GiftList;
